import React, { useState, useEffect } from 'react';
import { Container, Row, Col, Form, Button, Card, Alert, Spinner, Badge } from 'react-bootstrap';
import Select from 'react-select';
import { useAuth } from '../../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import { useNotifications } from '../../context/NotificationContext';
import { getMappings, saveMappings } from '../../api/bankParsingApi';
import { getHubspotContactProperties } from '../../api/fieldMappings';

const FieldMappings = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { success: notifySuccess, error: notifyError } = useNotifications();
  
  const [mappings, setMappings] = useState([]);
  const [hubspotProperties, setHubspotProperties] = useState([]);
  const [hubspotOptions, setHubspotOptions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  // Check if user is admin
  useEffect(() => {
    if (user && user.role !== 'admin' && user.role !== 'super_admin') {
      navigate('/');
    }
  }, [user, navigate]);

  // Fetch mappings and HubSpot properties on component mount
  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        setError(null);
        
        // Fetch HubSpot properties
        const properties = await getHubspotContactProperties();
        setHubspotProperties(properties);
        
        // Convert HubSpot properties to react-select options
        const options = properties.map(property => ({
          value: property.name,
          label: `${property.label} (${property.name})`,
          property: property
        }));
        setHubspotOptions(options);
        
        // Fetch existing mappings
        const data = await getMappings();
        setMappings(data.mappings || []);
      } catch (err) {
        console.error('Error fetching data:', err);
        setError('Failed to load field mappings and HubSpot properties');
        notifyError('Failed to load field mappings');
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  // Handle mapping input change (for react-select)
  const handleMappingChange = (fieldId, selectedOption) => {
    const hubspotProperty = selectedOption ? selectedOption.value : '';
    setMappings(prevMappings => 
      prevMappings.map(mapping => 
        mapping.fieldId === fieldId 
          ? { ...mapping, hubspotProperty }
          : mapping
      )
    );
  };

  // Handle save mappings
  const handleSave = async () => {
    try {
      setSaving(true);
      await saveMappings(mappings);
      notifySuccess('Field mappings saved successfully');
      setError(null);
    } catch (err) {
      console.error('Error saving mappings:', err);
      setError('Failed to save field mappings');
      notifyError('Failed to save field mappings');
    } finally {
      setSaving(false);
    }
  };

  // Check compatibility between field and hubspot property
  const checkCompatibility = (fieldType, hubspotProperty) => {
    if (!hubspotProperty) return { compatible: true, reason: '' };
    
    const propertyType = hubspotProperty.type.toLowerCase();
    
    // Most bank parsing fields will be numbers or text
    if (fieldType === 'text' && ['string', 'text', 'textarea'].includes(propertyType)) {
      return { compatible: true, reason: '' };
    }
    
    if (fieldType === 'number' && ['number', 'integer'].includes(propertyType)) {
      return { compatible: true, reason: '' };
    }
    
    // Special case for enumeration/select fields
    if (propertyType === 'enumeration' || propertyType.includes('select')) {
      return { 
        compatible: false, 
        reason: 'Select field requires exact match from options',
        warning: true
      };
    }
    
    // Default case - might work but with warning
    return { 
      compatible: true, 
      reason: `Type mismatch: ${fieldType} → ${propertyType}`,
      warning: true
    };
  };

  // Custom styles for React-Select
  const customStyles = {
    control: (provided) => ({
      ...provided,
      minHeight: '38px',
    }),
    menu: (provided) => ({
      ...provided,
      zIndex: 9999,
    }),
    menuPortal: (provided) => ({
      ...provided,
      zIndex: 9999
    })
  };

  // Group mappings by category for display
  const getGroupedMappings = () => {
    const groups = {
      'Financial Metrics': mappings.filter(m => 
        m.fieldId.includes('revenue') || 
        m.fieldId.includes('balance') || 
        m.fieldId.includes('debits')
      ),
      'Risk Metrics': mappings.filter(m => 
        m.fieldId.includes('negative') || 
        m.fieldId.includes('nsf')
      ),
      'Competitor Information': mappings.filter(m => 
        m.fieldId.includes('competitor')
      )
    };

    // Handle any ungrouped items
    const grouped = Object.values(groups).flat();
    const ungrouped = mappings.filter(m => !grouped.find(g => g.fieldId === m.fieldId));
    if (ungrouped.length > 0) {
      groups['Other'] = ungrouped;
    }

    return groups;
  };

  if (loading) {
    return (
      <Container className="mt-5 text-center">
        <Spinner animation="border" role="status">
          <span className="visually-hidden">Loading...</span>
        </Spinner>
        <p className="mt-2">Loading field mappings and HubSpot properties...</p>
      </Container>
    );
  }

  return (
    <Container fluid>
      <Row>
        <Col>
          <h3 className="mb-4">Bank Statement Parsing - Field Mappings</h3>
          
          {error && (
            <Alert variant="danger" className="mb-4">
              {error}
            </Alert>
          )}
          
          <Form onSubmit={(e) => { e.preventDefault(); handleSave(); }}>
            <Card>
              <Card.Header className="bg-light">
                <h5 className="mb-0">Bank Parsing Fields</h5>
              </Card.Header>
              <Card.Body>
                <Row className="mb-3 fw-bold">
                  <Col md={5}>Form Field</Col>
                  <Col md={7}>HubSpot Contact Property</Col>
                </Row>
                
                {mappings.map((mapping) => {
                  const selectedOption = mapping.hubspotProperty 
                    ? hubspotOptions.find(option => option.value === mapping.hubspotProperty) 
                    : null;
                  
                  const selectedProperty = selectedOption 
                    ? hubspotProperties.find(p => p.name === selectedOption.value) 
                    : null;
                  
                  // Determine field type based on field content
                  const fieldType = mapping.fieldId.includes('amount') || 
                                   mapping.fieldId.includes('revenue') || 
                                   mapping.fieldId.includes('balance') || 
                                   mapping.fieldId.includes('debits') ||
                                   mapping.fieldId.includes('nsf') ||
                                   mapping.fieldId.includes('negative') 
                                   ? 'number' : 'text';
                  
                  const compatibility = selectedProperty 
                    ? checkCompatibility(fieldType, selectedProperty) 
                    : { compatible: true, reason: '' };
                  
                  return (
                    <Row key={mapping.fieldId} className="mb-4 align-items-center mapping-row">
                      <Col md={5}>
                        <div>
                          <strong>{mapping.fieldLabel}</strong>
                          <div className="text-muted small">
                            Type: <Badge bg="secondary">
                              {fieldType === 'number' ? 'Number' : 'Text'}
                            </Badge>
                          </div>
                        </div>
                      </Col>
                      <Col md={7} className="select-container">
                        <Select
                          value={selectedOption}
                          onChange={(option) => handleMappingChange(mapping.fieldId, option)}
                          options={hubspotOptions}
                          isClearable
                          placeholder="Search or select a property..."
                          styles={customStyles}
                          className="basic-single"
                          classNamePrefix="select"
                          menuPosition="fixed"
                          menuPortalTarget={document.body}
                        />
                        
                        {selectedProperty && (
                          <div className="mt-2">
                            <div className="d-flex align-items-center gap-2">
                              <Badge bg={compatibility.compatible ? (compatibility.warning ? "warning" : "success") : "danger"}>
                                {selectedProperty.type.toLowerCase() === 'string' ? 'Text' : selectedProperty.type}
                              </Badge>
                              
                              {compatibility.reason && (
                                <span className={`small ${compatibility.warning ? 'text-warning' : (compatibility.compatible ? 'text-success' : 'text-danger')}`}>
                                  {compatibility.reason}
                                </span>
                              )}
                            </div>
                            
                            {selectedProperty.options && selectedProperty.options.length > 0 && (
                              <div className="mt-1 small text-muted">
                                Options: {selectedProperty.options.map(opt => opt.label || opt.value).join(', ')}
                              </div>
                            )}
                          </div>
                        )}
                      </Col>
                    </Row>
                  );
                })}

                {mappings.length === 0 && (
                  <Alert variant="info">
                    No field mappings configured. The system will initialize default fields on first save.
                  </Alert>
                )}
              </Card.Body>
            </Card>
            
            <div className="d-flex justify-content-between align-items-center mt-4">
              <Alert variant="info" className="d-inline-block mb-0">
                <strong>{mappings.filter(m => m.hubspotProperty).length}</strong> of {mappings.length} fields mapped
              </Alert>
              <Button 
                type="submit" 
                variant="primary" 
                size="lg"
                disabled={saving}
                className="d-flex align-items-center gap-2"
              >
                {saving && <Spinner animation="border" size="sm" />}
                {saving ? 'Saving...' : 'Save Mappings'}
              </Button>
            </div>
          </Form>
        </Col>
      </Row>
    </Container>
  );
};

export default FieldMappings;
